// PBR material factory for the awning scene.
// Mirrors the look-and-feel approach of the pergola configurator's materials cache:
// reused across rebuilds, environment-map aware, calibrated for ACES tone mapping.

import * as THREE from 'three';

// ── Procedural environment fallback (matches pergola's gradient sky) ──
function buildProceduralEnvMap() {
  const size = 256;
  const data = new Float32Array(size * size * 4);
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const i = (y * size + x) * 4;
      const ny = y / size;
      data[i + 0] = 0.6 + ny * 0.4;
      data[i + 1] = 0.7 + ny * 0.3;
      data[i + 2] = 0.85 + ny * 0.15;
      data[i + 3] = 1.0;
    }
  }
  const tex = new THREE.DataTexture(data, size, size, THREE.RGBAFormat, THREE.FloatType);
  tex.mapping = THREE.EquirectangularReflectionMapping;
  tex.needsUpdate = true;
  return tex;
}

// Default repeat for fabric textures across an awning sized roughly 3m × 3m.
const FABRIC_REPEAT_X = 8;
const FABRIC_REPEAT_Y = 6;

// Patio repeat — patio is fixed at 30 ft × 18 ft (9.144 m × 5.486 m). With
// these values each texture tile spans roughly 5 ft × 4.5 ft → planks read
// at ~12-14 inches wide on the rendered surface. Plank long-axis (after the
// patio's −π/2 X rotation) runs along world Z (depth, perpendicular to wall).
const PATIO_REPEAT_X = 6;
const PATIO_REPEAT_Y = 4;

export class AwningMaterials {
  constructor(renderer = null) {
    this.envMap = buildProceduralEnvMap();
    this._fabricTextureSet = null;

    // Anodized aluminum — used for arm tubes and front bar.
    this.aluminum = new THREE.MeshStandardMaterial({
      color: 0xc8c9cb,
      roughness: 0.32,
      metalness: 0.85,
      envMapIntensity: 1.0,
    });

    // Powder-coat black — used for brackets (matches the photos Dana shared).
    this.powderCoat = new THREE.MeshPhysicalMaterial({
      color: 0x1c1c1c,
      roughness: 0.55,
      metalness: 0.4,
      clearcoat: 0.15,
      clearcoatRoughness: 0.55,
      envMapIntensity: 0.7,
    });

    // Cassette housing — same powder-coat profile but slightly lighter.
    this.cassette = new THREE.MeshPhysicalMaterial({
      color: 0xd8d4ce,
      roughness: 0.45,
      metalness: 0.55,
      clearcoat: 0.2,
      clearcoatRoughness: 0.5,
      envMapIntensity: 0.85,
    });

    // House wall — neutral stucco/siding.
    this.wall = new THREE.MeshStandardMaterial({
      color: 0xe8e1d3,
      roughness: 0.92,
      metalness: 0.0,
    });

    // Patio — flat light warm grey. The wood-deck PBR set (see
    // _loadPatioTextureSet) didn't read well against shadows in the current
    // lighting setup, so we're back to a matte tone that gives shadows
    // automatic contrast. Texture loader is kept on the class for a later revisit.
    this.patio = new THREE.MeshStandardMaterial({
      color: 0xc8c5be,
      roughness: 0.92,
      metalness: 0.0,
    });

    // Fabric — physically-based canvas with real PBR maps.
    // Per-fabric color tint multiplies against the albedo map, so the canvas
    // weave detail stays while the SKU's hex shifts the overall hue.
    const tex = this._loadFabricTextureSet(renderer);
    this.fabric = new THREE.MeshPhysicalMaterial({
      color: 0xffffff,                                 // white base — tinted via setFabricColor
      map: tex.albedoMap,
      normalMap: tex.normalMap,
      normalScale: new THREE.Vector2(0.6, 0.6),
      roughnessMap: tex.roughnessMap,
      roughness: 0.85,
      aoMap: tex.aoMap,
      aoMapIntensity: 0.7,
      metalness: 0.0,
      sheen: 0.4,
      sheenRoughness: 0.85,
      sheenColor: new THREE.Color(0xffffff),
      side: THREE.DoubleSide,
      envMapIntensity: 0.5,
    });

    // ── Lifestyle materials (door + interior + furniture) ─────────────
    this.doorFrame = new THREE.MeshPhysicalMaterial({
      color: 0x2a2c2f,
      roughness: 0.45,
      metalness: 0.6,
      clearcoat: 0.2,
      clearcoatRoughness: 0.5,
      envMapIntensity: 0.85,
    });

    this.glass = new THREE.MeshPhysicalMaterial({
      color: 0xc9d2d8,
      roughness: 0.04,
      metalness: 0.0,
      transmission: 0.95,
      transparent: true,
      opacity: 0.55,
      ior: 1.5,
      thickness: 0.2,
      envMapIntensity: 1.2,
    });

    this.interior = new THREE.MeshStandardMaterial({
      color: 0x9a8770,
      roughness: 0.85,
      metalness: 0.0,
    });

    this.wicker = new THREE.MeshStandardMaterial({
      color: 0x4a4540,
      roughness: 0.95,
      metalness: 0.0,
    });

    this.cushion = new THREE.MeshStandardMaterial({
      color: 0xd9cdb5,
      roughness: 0.85,
      metalness: 0.0,
    });

    this.pillow = new THREE.MeshStandardMaterial({
      color: 0xe9e2d2,
      roughness: 0.78,
      metalness: 0.0,
    });

    this.tableTop = new THREE.MeshStandardMaterial({
      color: 0xe0d8c8,
      roughness: 0.7,
      metalness: 0.0,
    });

    // Drop-screen fabric — near-black mesh with ~7% openness. Plain opacity
    // approximates the see-through screen look; can swap to alphaTest with
    // a screen-pattern alphaMap later for higher fidelity.
    this.dropScreen = new THREE.MeshPhysicalMaterial({
      color: 0x1a1a1a,
      side: THREE.DoubleSide,
      transparent: true,
      opacity: 0.93,
      roughness: 0.85,
      metalness: 0.0,
    });

    this._all = [
      this.aluminum, this.powderCoat, this.cassette,
      this.wall, this.patio, this.fabric,
      this.doorFrame, this.glass, this.interior,
      this.wicker, this.cushion, this.pillow, this.tableTop,
      this.dropScreen,
    ];
  }

  // Load the canvas weave PBR set once and cache it.
  // renderer (optional): used to query max anisotropy; falls back to 16.
  _loadFabricTextureSet(renderer = null) {
    if (this._fabricTextureSet) return this._fabricTextureSet;

    const aniso = (renderer && renderer.capabilities)
      ? renderer.capabilities.getMaxAnisotropy()
      : 16;
    const loader = new THREE.TextureLoader();
    const basePath = (typeof import.meta !== 'undefined' && import.meta.env)
      ? (import.meta.env.BASE_URL || '/')
      : '/';
    const dir = `${basePath.replace(/\/$/, '')}/awning/fabrics/canvas/`;

    const configure = (tex, isColor) => {
      tex.wrapS = THREE.RepeatWrapping;
      tex.wrapT = THREE.RepeatWrapping;
      tex.anisotropy = aniso;
      if (isColor) tex.colorSpace = THREE.SRGBColorSpace;
      tex.repeat.set(FABRIC_REPEAT_X, FABRIC_REPEAT_Y);
      return tex;
    };

    const albedoMap    = configure(loader.load(`${dir}fabric_pattern_07_col_1_2k.jpg`),    true);
    const normalMap    = configure(loader.load(`${dir}fabric_pattern_07_nor_gl_2k.jpg`),   false);
    const roughnessMap = configure(loader.load(`${dir}fabric_pattern_07_rough_2k.jpg`),    false);
    const aoMap        = configure(loader.load(`${dir}fabric_pattern_07_ao_2k.jpg`),       false);

    this._fabricTextureSet = { albedoMap, normalMap, roughnessMap, aoMap };
    return this._fabricTextureSet;
  }

  // Load the wood-deck PBR set once and cache it. Same shape as the fabric
  // loader; uses a different file dir + repeat scale so plank size reads
  // realistically on the 30 × 18 ft patio.
  _loadPatioTextureSet(renderer = null) {
    if (this._patioTextureSet) return this._patioTextureSet;

    const aniso = (renderer && renderer.capabilities)
      ? renderer.capabilities.getMaxAnisotropy()
      : 16;
    const loader = new THREE.TextureLoader();
    const basePath = (typeof import.meta !== 'undefined' && import.meta.env)
      ? (import.meta.env.BASE_URL || '/')
      : '/';
    const dir = `${basePath.replace(/\/$/, '')}/awning/patio/wood-deck/`;

    const configure = (tex, isColor) => {
      tex.wrapS = THREE.RepeatWrapping;
      tex.wrapT = THREE.RepeatWrapping;
      tex.anisotropy = aniso;
      if (isColor) tex.colorSpace = THREE.SRGBColorSpace;
      tex.repeat.set(PATIO_REPEAT_X, PATIO_REPEAT_Y);
      return tex;
    };

    const albedoMap    = configure(loader.load(`${dir}wood_planks_grey_diff_2k.jpg`),    true);
    const normalMap    = configure(loader.load(`${dir}wood_planks_grey_nor_gl_2k.jpg`),  false);
    const roughnessMap = configure(loader.load(`${dir}wood_planks_grey_rough_2k.jpg`),   false);
    const aoMap        = configure(loader.load(`${dir}wood_planks_grey_ao_2k.jpg`),      false);

    this._patioTextureSet = { albedoMap, normalMap, roughnessMap, aoMap };
    return this._patioTextureSet;
  }

  attachEnvMap(envMap) {
    const env = envMap || this.envMap;
    for (const m of this._all) {
      m.envMap = env;
      m.needsUpdate = true;
    }
  }

  // Set fabric color via hex (number) or CSS string. Multiplies against the
  // albedo map so the canvas weave detail is preserved while the SKU's hue
  // shifts the overall fabric color.
  setFabricColor(hex) {
    if (hex == null) return;
    if (typeof hex === 'number') this.fabric.color.setHex(hex);
    else this.fabric.color.set(hex);
  }

  // Apply a fabric record from /src/awning/data/fabrics-sunbrella-shade.json.
  // The texture set is permanent; only the color tint changes per SKU.
  setFabric(record /*, textureLoader (deprecated) */) {
    if (!record) return;
    if (record.hex) this.setFabricColor(record.hex);
  }
}
